import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  collection,
  addDoc,
  doc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  writeBatch,
} from "firebase/firestore";
import { getDownloadURL, ref as storageRef, uploadBytes } from "firebase/storage";
import { auth, firestore, storage } from "../../firebaseConfig";
import {
  buildResponderInitials,
  buildThreadId,
  canDeleteForAll,
  isMessageModerationAllowed,
  MAX_MESSAGE_FILE_SIZE_BYTES,
  statusLabel,
} from "./helpers";

const FILE_FALLBACK_TEXT = "Sent a file";
const MODERATION_ENDPOINT = process.env.REACT_APP_FUNCTIONS_URL
  ? `${process.env.REACT_APP_FUNCTIONS_URL}/moderate_message`
  : null;

const LONG_PRESS_MS = 500;

const MessageBubble = ({ message, actorType, onLongPressDelete }) => {
  const isMine = actorType === "company" ? message.sender === "company" : message.sender !== "company";
  const deleted =
    (actorType === "company" && message.deletedForCompany) ||
    (actorType !== "company" && message.deletedForParticipant);
  const longPressTimeoutRef = useRef(null);

  const clearLongPressTimeout = () => {
    if (longPressTimeoutRef.current) {
      window.clearTimeout(longPressTimeoutRef.current);
      longPressTimeoutRef.current = null;
    }
  };

  const startLongPress = () => {
    if (deleted) return;
    clearLongPressTimeout();
    longPressTimeoutRef.current = window.setTimeout(() => {
      onLongPressDelete?.(message);
      longPressTimeoutRef.current = null;
    }, LONG_PRESS_MS);
  };

  return (
    <div className={`flex ${isMine ? "justify-end" : "justify-start"} mb-3`}>
      <div
        className={`max-w-[80%] rounded-2xl px-4 py-2 text-sm shadow-sm border transition ${
          deleted
            ? "bg-gray-100 text-gray-500 border-gray-200 italic"
            : isMine
            ? "bg-green-50 border-green-100"
            : "bg-white border-slate-200"
        }`}
        onMouseDown={startLongPress}
        onMouseUp={clearLongPressTimeout}
        onMouseLeave={clearLongPressTimeout}
        onTouchStart={startLongPress}
        onTouchEnd={clearLongPressTimeout}
        onTouchCancel={clearLongPressTimeout}
        onContextMenu={(e) => {
          e.preventDefault();
          onLongPressDelete?.(message);
        }}
      >
        <div className="flex items-center justify-between gap-2 text-[11px] text-slate-500 mb-1">
          <span className="capitalize">
            {message.sender === "company"
              ? `Company${message.companyResponderInitials ? ` (${message.companyResponderInitials})` : ""}`
              : actorType === "company"
              ? "Participant"
              : "You"}
          </span>
          <span>{statusLabel(message, actorType)}</span>
        </div>
        <div className={`${deleted ? "line-through" : ""} text-black dark:text-orange-300`}>
          {deleted ? "Message deleted" : message.text}
        </div>
        {!deleted && message?.file?.fileName && (
          <div className="mt-2 text-xs">
            <a
              className="text-emerald-700 hover:underline"
              href={message.file.fileUrl || "#"}
              target="_blank"
              rel="noreferrer"
            >
              📎 {message.file.fileName}
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

const MessagingPanel = ({
  companyName,
  participantId,
  actorType = "participant",
  title,
  disclaimer,
  onConversationDeleted,
  containerClassName = "",
}) => {
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [sending, setSending] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [fileAttachment, setFileAttachment] = useState(null);
  const [deleteTargetMessage, setDeleteTargetMessage] = useState(null);
  const scrollRef = useRef(null);

  const threadId = useMemo(() => {
    if (!companyName || !participantId) return null;
    return buildThreadId(companyName, participantId);
  }, [companyName, participantId]);

  useEffect(() => {
    if (!firestore || !threadId) return undefined;

    const q = query(
      collection(firestore, "companyMessages", threadId, "messages"),
      orderBy("createdAt", "asc")
    );
    const unsub = onSnapshot(q, (snap) => {
      const next = [];
      snap.forEach((d) => next.push({ id: d.id, ...d.data() }));
      setMessages(next);
    });
    return () => unsub();
  }, [threadId]);

  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  useEffect(() => {
    if (!firestore || !threadId) return;
    const toMark = messages.filter((m) => {
      if (actorType === "company") {
        return m.sender !== "company" && !m.readByCompany;
      }
      return m.sender === "company" && !m.readByParticipant;
    });
    if (!toMark.length) return;

    Promise.all(
      toMark.map(async (m) => {
        try {
          const ref = doc(firestore, "companyMessages", threadId, "messages", m.id);
          await updateDoc(ref, {
            readByParticipant: actorType !== "company" ? true : m.readByParticipant || false,
            readByCompany: actorType === "company" ? true : m.readByCompany || false,
          });
        } catch (err) {
          // best-effort
        }
      })
    );
  }, [messages, actorType, threadId]);

  const sendMessage = async () => {
    if ((!input.trim() && !fileAttachment) || !firestore || !threadId) return;
    setSending(true);
    try {
      const messageText = input.trim() || FILE_FALLBACK_TEXT;
      const companyResponderInitials =
        actorType === "company"
          ? buildResponderInitials(auth?.currentUser?.displayName || auth?.currentUser?.email || "")
          : "";

      if (actorType === "company" && MODERATION_ENDPOINT) {
        const moderationRes = await fetch(MODERATION_ENDPOINT, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            text: messageText,
            companyName,
            participantId,
            threadId,
          }),
        });
        const moderationJson = await moderationRes.json();
        const moderationResult =
          moderationJson?.result || moderationJson?.data?.result || moderationJson?.data || moderationJson;
        const moderationAllowed = isMessageModerationAllowed(moderationResult);

        if (!moderationAllowed) {
          const companyKey = String(companyName || "").trim().replace(/\//g, "-");
          const statusReason =
            moderationResult?.reason || "Blocked due to inappropriate company messaging content";

          if (companyKey) {
            await setDoc(
              doc(firestore, "companies", companyKey),
              {
                subscriptionStatus: "blocked",
                subscriptionStatusReason: statusReason,
                status_reason: statusReason,
                is_subscription_active: false,
                can_operate: false,
                can_write_data: false,
              },
              { merge: true }
            );
            await setDoc(
              doc(firestore, "companies", companyKey, "subscriptionSnapshots", "current"),
              {
                status: "blocked",
                status_reason: statusReason,
                subscriptionStatus: "blocked",
                is_subscription_active: false,
                can_operate: false,
                can_write_data: false,
                updatedAt: serverTimestamp(),
              },
              { merge: true }
            );
          }

          await addDoc(collection(firestore, "messageModerationIncidents"), {
            companyName,
            companyKey,
            participantId,
            threadId,
            actorType,
            messageText,
            companyResponderInitials,
            moderation: moderationResult || null,
            status: "blocked",
            reason: statusReason,
            createdAt: serverTimestamp(),
          });

          window.alert(`Message blocked: ${statusReason}`);
          setSending(false);
          return;
        }
      }

      const threadRef = doc(firestore, "companyMessages", threadId);
      await setDoc(
        threadRef,
        {
          companyName,
          participantId,
          archivedForCompany: false,
          archivedForParticipant: false,
          lastMessageAt: serverTimestamp(),
        },
        { merge: true }
      );

      let filePayload = null;
      if (fileAttachment && storage) {
        if (fileAttachment.size > MAX_MESSAGE_FILE_SIZE_BYTES) {
          window.alert("File is too large. Maximum allowed size is 5MB.");
          setSending(false);
          return;
        }
        const path = `companyMessages/${threadId}/${Date.now()}_${fileAttachment.name}`;
        const ref = storageRef(storage, path);
        await uploadBytes(ref, fileAttachment);
        const url = await getDownloadURL(ref);
        filePayload = { fileName: fileAttachment.name, fileUrl: url };
      }

      await addDoc(collection(firestore, "companyMessages", threadId, "messages"), {
        text: messageText,
        file: filePayload,
        sender: actorType === "company" ? "company" : "participant",
        companyResponderInitials: actorType === "company" ? companyResponderInitials : null,
        createdAt: serverTimestamp(),
        readByCompany: actorType === "company",
        readByParticipant: actorType !== "company",
        deletedForCompany: false,
        deletedForParticipant: false,
      });
      setInput("");
      setFileAttachment(null);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("send message failed", err);
    } finally {
      setSending(false);
    }
  };

  const deleteForMe = async (message) => {
    if (!firestore || !threadId || !message?.id) return;
    try {
      const ref = doc(firestore, "companyMessages", threadId, "messages", message.id);
      await updateDoc(ref, {
        deletedForParticipant: actorType !== "company" ? true : message.deletedForParticipant,
        deletedForCompany: actorType === "company" ? true : message.deletedForCompany,
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("delete for me failed", err);
    }
  };

  const deleteForAll = async (message) => {
    if (!firestore || !threadId || !message?.id) return;
    if (!canDeleteForAll(message, actorType)) return;
    try {
      const ref = doc(firestore, "companyMessages", threadId, "messages", message.id);
      await updateDoc(ref, {
        deletedForParticipant: true,
        deletedForCompany: true,
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("delete for all failed", err);
    }
  };

  const clearConversation = async () => {
    if (!firestore || !threadId) return;
    setClearing(true);
    try {
      const batch = writeBatch(firestore);
      const threadRef = doc(firestore, "companyMessages", threadId);

      const meta = {
        archivedForCompany: actorType === "company",
        archivedForParticipant: actorType !== "company",
        lastClearedBy: actorType,
      };
      batch.set(threadRef, meta, { merge: true });

      const snap = await getDocs(collection(firestore, "companyMessages", threadId, "messages"));
      snap.forEach((m) => {
        const ref = doc(firestore, "companyMessages", threadId, "messages", m.id);
        batch.update(ref, {
          deletedForCompany: true,
          deletedForParticipant: true,
        });
      });

      await batch.commit();
      setMessages([]);
      onConversationDeleted?.(threadId);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Failed to clear conversation", err);
    } finally {
      setClearing(false);
    }
  };

  if (!companyName || !participantId) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 text-amber-800 p-4">
        Unable to start messaging without company and participant context.
      </div>
    );
  }

  return (
    <div className={`rounded-2xl border border-slate-200 bg-white shadow-sm ${containerClassName}`}>
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <div>
          <p className="text-sm font-semibold text-black dark:text-orange-300">
            {title || "Secure Messaging"}
          </p>
          <p className="text-xs text-slate-500">
            AI monitored for content violations. Accounts may be suspended for abuse.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={clearConversation}
            disabled={clearing || messages.length === 0}
            className="text-[11px] text-red-700 bg-red-50 px-2 py-1 rounded-full border border-red-100 disabled:opacity-50"
          >
            {clearing ? "Deleting…" : "Delete chat"}
          </button>
          <span className="text-[11px] text-emerald-700 bg-emerald-50 px-2 py-1 rounded-full border border-emerald-100">
            Company chat
          </span>
        </div>
      </div>

      {disclaimer && (
        <div className="bg-amber-50 text-amber-800 text-xs px-4 py-3 border-b border-amber-100">
          {disclaimer}
        </div>
      )}

      <div ref={scrollRef} className="h-72 overflow-y-auto px-4 py-3 space-y-2 bg-slate-50">
        {messages.length === 0 && (
          <div className="text-center text-xs text-slate-500 py-8">
            Start the conversation with your company. Status updates will show once they read.
          </div>
        )}
        {messages
          .filter((m) =>
            actorType === "company" ? !m.deletedForCompany : !m.deletedForParticipant
          )
          .map((message) => (
            <MessageBubble
              key={message.id}
              message={message}
              actorType={actorType}
              onLongPressDelete={setDeleteTargetMessage}
            />
          ))}
      </div>

      <div className="border-t border-slate-100 px-4 py-3 flex items-center gap-2">
        {actorType === "company" && (
          <label className="text-xs text-slate-600 px-2 py-1 border border-dashed border-slate-300 rounded-lg cursor-pointer bg-slate-50 hover:bg-slate-100">
            <input
              type="file"
              className="hidden"
              onChange={(e) => {
                const nextFile = e.target.files?.[0] || null;
                if (nextFile && nextFile.size > MAX_MESSAGE_FILE_SIZE_BYTES) {
                  window.alert("File is too large. Maximum allowed size is 5MB.");
                  setFileAttachment(null);
                  e.target.value = "";
                  return;
                }
                setFileAttachment(nextFile);
              }}
            />
            {fileAttachment ? `Attached: ${fileAttachment.name}` : "Attach file"}
          </label>
        )}
        <input
          type="text"
          className="flex-1 rounded-full border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200"
          placeholder="Type a message…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") sendMessage();
          }}
        />
        <button
          type="button"
          onClick={sendMessage}
          disabled={sending || (!input.trim() && !fileAttachment)}
          className="px-4 py-2 rounded-full bg-emerald-600 text-white text-sm font-semibold disabled:bg-emerald-200"
        >
          {sending ? "Sending…" : "Send"}
        </button>
      </div>
      {deleteTargetMessage && (
        <div className="fixed inset-0 z-50 bg-black/45 flex items-end sm:items-center justify-center p-4">
          <div className="w-full max-w-sm rounded-2xl border border-slate-200 bg-white p-4 shadow-xl">
            <p className="text-sm font-semibold text-black dark:text-orange-300">
              Delete message
            </p>
            <p className="mt-1 text-xs text-slate-500">
              Choose what to delete for this message.
            </p>
            <div className="mt-4 space-y-2">
              <button
                type="button"
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-black dark:text-orange-300"
                onClick={() => {
                  deleteForMe(deleteTargetMessage);
                  setDeleteTargetMessage(null);
                }}
              >
                Delete for me
              </button>
              {canDeleteForAll(deleteTargetMessage, actorType) && (
                <button
                  type="button"
                  className="w-full rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
                  onClick={() => {
                    deleteForAll(deleteTargetMessage);
                    setDeleteTargetMessage(null);
                  }}
                >
                  Delete for both
                </button>
              )}
              <button
                type="button"
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-600"
                onClick={() => setDeleteTargetMessage(null)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MessagingPanel;
